import {
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Button,
  Modal,
  CardMedia,
} from "@mui/material";
import pillsContainerImg from "../../../assets/Flacone.svg";
import dayjs from "dayjs";
import { UseFormReturn } from "react-hook-form";
import RecurringBookingFormInterface, {
  RecurringBookingFormConfirm,
} from "../../../services/recurringBookingInterfaces";
import { useContext, useEffect } from "react";
import { RecurringBookingContext } from "../Context/RecurringBookingContextWrapper";
import { valPren } from "../../../networking";

const RecurringBookingSummaryTable = (props: {
  methodsMain?: UseFormReturn<RecurringBookingFormInterface>;
  methodsDetail?: UseFormReturn<RecurringBookingFormConfirm>;
  handlePageReset: Function;
  viewSummary: boolean;
  numeroPrenotazione: string;
  handleSummaryTableOpening: Function;
}) => {
  const context = useContext(RecurringBookingContext);

  // Questo useEffect parte ogni volta che il riepilogo viene aperto (viewSummary = true).
  // Se l’importo totale non è stato ancora calcolato (es. "0,00 €" o stringa vuota),
  // costruisce il payload per valPren e aggiorna il context con l’importo restituito.
  useEffect(() => {
    // 1) Se la modale non è aperta, non fare nulla (evitiamo chiamate inutili).
    if (!props.viewSummary) return;

    // 2) Se per qualche motivo il context non è disponibile, usciamo.
    if (!context) return;

    // 3) Flag per evitare di fare setState se il componente si unmounta
    //    mentre la promise è ancora in corso (pattern di cleanup "cancelled").
    let cancelled = false;

    // 4) Helper: interpreta la stringa importo (es. "0,00 €") e la converte in numero.
    //    Serve per capire se l’importo è già calcolato (> 0) oppure no.
    const isZeroImporto = (val?: string) => {
      if (!val) return true;
      const n = Number(
        val
          .replace(/[^\d,.-]/g, "") // lascia solo cifre e separatori
          .replace(/\./g, "") // rimuovi separatori delle migliaia
          .replace(",", ".") // converte la virgola in punto
      );
      return Number.isNaN(n) || n <= 0;
    };

    // 5) Tipi minimi per i campi che leggiamo dalla form principale.
    //    Non importiamo tipi globali: descriviamo solo ciò che ci serve qui.
    type Dettaglio = {
      numeroCicli?: number | string;
      ticket?: {
        CODICEPOSTIC?: string;
        codice?: string;
        codicepostic?: string;
      };
      ente?: { codice?: string; CODICE?: string; codiceEnte?: string };
      medico?: { CODICE?: string; CODICEMEDICO?: string };
      specialita?: { SPECIALITA?: string };
      prestazione?: { PRESTAZIONE?: string };
      tipologiaAgenda?: { CODICE?: string };
    };

    // 6) Piccoli helper per estrarre codici dai vari oggetti (gestiscono i possibili alias).
    const pickCodEnte = (ente?: Dettaglio["ente"]) =>
      ente?.codice ?? ente?.CODICE ?? ente?.codiceEnte ?? "";

    //    Per il medico usiamo per prima scelta quello selezionato in form,
    //    e solo in fallback la risorsa dell’appuntamento restituita dallo sviluppo terapia.
    const pickMedico = (det?: Dettaglio, app?: any) =>
      det?.medico?.CODICE ??
      det?.medico?.CODICEMEDICO ??
      app?.codiceRisorsa ??
      app?.CODICERISORSA ??
      "";

    // 7) Funzione che esegue davvero il calcolo quando serve.
    const run = async () => {
      // Se l’importo è già stato calcolato (> 0), non rifacciamo la chiamata.
      if (!isZeroImporto(context.importoTotale)) return;

      // Leggiamo l’istanza dettagli dalla form principale (arriva via props),
      // ci serve per ricavare quantita (numeroCicli), ticket ed ente.
      const istanzaDettaglio =
        (props.methodsMain?.getValues(
          "recurringBooking.istanzaSchedaDettaglio"
        ) as Dettaglio[]) ?? [];

      // Le prestazioni e i relativi appuntamenti sono già state mappate nel context
      // (confermaPianoTerapiaBody). Ogni elemento ha gli appuntamenti selezionati.
      const prestazioniOrig =
        context.confermaPianoTerapiaBody?.prestazioni ?? [];

      // 8) Costruiamo il payload di valPren esattamente come vuole l’API:
      //    - una riga per ogni appuntamento selezionato
      //    - campi obbligatori sempre presenti
      //    - campi opzionali presenti anche se vuoti
      const payload = {
        prestazioni: prestazioniOrig.flatMap((p: any, idx: number) =>
          (p.appuntamenti ?? []).map((app: any) => {
            // mappiamo per indice: la i-esima prestazione corrisponde
            // al i-esimo dettaglio compilato nella form principale
            const det = istanzaDettaglio[idx];
            return {
              // SSN (se sono vuoti resta payload compatibile)
              numImpegnativa: p.impegnativa ?? "",
              dataImpegnativa: p.dataImpegnativa ?? "",

              // obbligatori (sempre valorizzati)
              dataPrenotazione: dayjs().format("YYYY-MM-DD"),
              dataAppuntamento: app.dataApp ?? "",
              codSpecia: p.specialita,
              codAnapre: p.prestazione,
              codTpgpre: p.tipologia,
              codMedint: pickMedico(det, app),
              quantita: String(det?.numeroCicli ?? 1),

              // opzionali ma richiesti come chiavi
              codPostic: det?.ticket?.CODICEPOSTIC ?? "",
              codConven: "",
              codEnte: pickCodEnte(det?.ente),
            };
          })
        ),
      };

      try {
        // 9) valPren restituisce un AxiosResponse => i dati reali stanno in "data".
        const { data } = await valPren(payload);

        // 10) Aggiorniamo il context solo se:
        //     - la modale è ancora aperta (non cancellata),
        //     - la risposta è OK.
        if (!cancelled && data?.esito === "OK") {
          console.log({data});
          // La backend può restituire "importoPaziente" o "importoTotale".
          // Se non c’è, fallback a "0,00 €".
          context.setImportoTotale(
            data.importoPaziente ?? data.importoTotale ?? "0,00 €"
          );
        }
      } catch (e) {
        // 11) Se la chiamata fallisce, non interrompiamo il rendering della modale,
        //     semplicemente lasciamo l’importo a 0 e logghiamo per debug.
        console.error("valPren (riepilogo) errore:", e);
      }
    };

    run();

    // 12) Cleanup: se il componente si smonta (o la modale si chiude)
    //     segniamo "cancelled" così evitiamo setState dopo l’unmount.
    return () => {
      cancelled = true;
    };
    // Dipendenze:
    // - parte quando apri/chiudi la modale
    // - oppure se cambia il body nel context (es. hai cambiato selezioni)
    // - oppure se cambia il methodsMain passato (edge case)
  }, [props.viewSummary, context?.confermaPianoTerapiaBody, props.methodsMain]);

  return (
    <Modal open={props.viewSummary}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1300,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            padding: 4,
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            maxWidth: 850,
            width: "100%",
            maxHeight: "90vh",
          }}
        >
          {/* TITOLO */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              marginBottom: 2,
              textAlign: "left",
              color: "#01579B",
            }}
          >
            Scheda Riepilogo Prenotazione
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}
          >
            <CardMedia
              component="img"
              sx={{
                opacity: "100%",
                height: {
                  xs: "0vh",
                  sm: "12vh",
                  md: "14vh",
                },
                objectFit: "contain", // o "scale-down"
              }}
              image={pillsContainerImg}
            />
          </Box>

          {/* DETTAGLI PRENOTAZIONE */}
          <Box
            sx={{
              backgroundColor: "#F4F4F4",
              padding: 2,
              borderRadius: "4px",
              marginBottom: 2,
              border: "1px solid rgba(204, 204, 204, 0.80)",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Numero Prenotazione
                  </Typography>
                  <Typography>{`${props.numeroPrenotazione}`}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Data Prenotazione
                  </Typography>
                  <Typography>{dayjs().format("DD/MM/YYYY")}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Operatore
                  </Typography>
                  <Typography>Non selezionato</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Ente
                  </Typography>
                  {
                    <Typography>{`${
                      props.methodsMain?.getValues(
                        "recurringBooking.istanzaSchedaDettaglio.0.ente"
                      ) ?? "Non selezionato"
                    }`}</Typography>
                  }
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Nominativo Paziente
                  </Typography>
                  <Typography>{`${
                    props.methodsDetail
                      ?.getValues("cognomePaziente")
                      ?.concat(
                        " ",
                        props.methodsDetail?.getValues("nomePaziente") ?? ""
                      ) ?? "Non selezionato"
                  }`}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Tipologia Agenda
                  </Typography>
                  <Typography>{`${
                    props.methodsMain?.getValues(
                      "recurringBooking.istanzaSchedaDettaglio.0.tipologiaAgenda"
                    ).DESCRIZIONE ?? "Non selezionato"
                  }`}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography fontWeight="bold" color="#01579B">
                    Prezzo
                  </Typography>
                  <Typography> {context?.importoTotale + " €"} </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sm={3} />
            </Grid>

            <Grid item md={12}>
              <TableContainer
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignSelf: "stretch",
                  borderRadius: "4px",
                  borderTop: "1px solid #CCC",
                  borderBottom: "1px solid #CCC",
                  background: "#F9F9F9",
                  marginTop: 2,
                  overflow: "auto",
                  overflowY: "auto",
                  maxHeight: "200px",
                }}
              >
                <Table sx={{ tableLayout: "fixed", minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#E0E0E0" }}>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        N.
                      </TableCell>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        Prestazione
                      </TableCell>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        Medico
                      </TableCell>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        Data
                      </TableCell>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        Ora
                      </TableCell>
                      <TableCell
                        sx={{
                          background: "rgba(0, 0, 0, 0.00)",
                          borderRight: "1px solid #CCC",
                          borderLeft: "1px solid #CCC",
                          borderBottom: "1px solid #CCC",
                          color: "#000000",
                          fontStyle: "normal",
                        }}
                      >
                        Durata
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {context?.confermaPianoTerapiaBody?.prestazioni
                      ? context.confermaPianoTerapiaBody.prestazioni.map(
                          (e, progressivoIndex) =>
                            (e.appuntamenti ?? []).map((appointment, idx) => (
                              <TableRow
                                key={`${progressivoIndex}-${
                                  appointment.idAppuntamento ?? idx
                                }`}
                              >
                                <Tooltip title="numero" placement="top-start">
                                  <TableCell
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      borderRight: "1px solid #CCC",
                                      borderLeft: "1px solid #CCC",
                                    }}
                                  >
                                    {progressivoIndex + 1}
                                  </TableCell>
                                </Tooltip>

                                <Tooltip
                                  title={e.prestazione ?? "Non pervenuta"}
                                  placement="top-start"
                                >
                                  <TableCell
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      borderRight: "1px solid #CCC",
                                      borderLeft: "1px solid #CCC",
                                    }}
                                  >
                                    {e.prestazione ?? "Non pervenuta"}
                                  </TableCell>
                                </Tooltip>

                                <Tooltip
                                  title={appointment.descrisorsa}
                                  placement="top-start"
                                >
                                  <TableCell
                                    sx={{
                                      maxWidth: 160,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      borderRight: "1px solid #CCC",
                                    }}
                                  >
                                    {appointment.descrisorsa}
                                  </TableCell>
                                </Tooltip>

                                <TableCell
                                  sx={{ borderRight: "1px solid #CCC" }}
                                >
                                  {dayjs(appointment.dataApp).isValid()
                                    ? dayjs(appointment.dataApp).format(
                                        "DD/MM/YYYY"
                                      )
                                    : "—"}
                                </TableCell>
                                <TableCell
                                  sx={{ borderRight: "1px solid #CCC" }}
                                >
                                  {appointment.oraApp ?? "—"}
                                </TableCell>
                                <TableCell
                                  sx={{ borderRight: "1px solid #CCC" }}
                                >
                                  {appointment.durata ?? "—"}
                                </TableCell>
                              </TableRow>
                            ))
                        )
                      : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Box>

          <Grid
            container
            justifyContent="space-between"
            spacing={2}
            sx={{ marginTop: 2 }}
          >
            <Grid item>
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#2E83C4",
                  color: "#2E83C4",
                  "&:hover": { backgroundColor: "#2E83C4", color: "white" },
                }}
              >
                Stampa
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#2E83C4" }}
                onClick={() => {
                  props.handleSummaryTableOpening();
                  props.handlePageReset();
                }}
              >
                Chiudi
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default RecurringBookingSummaryTable;
